// A child's blueprint as forms (02-DESIGN/13-blueprints/01-the-blueprint.md
// § the document, § the console: Workspaces; 03-required.md): each section
// of the document as the fields a person fills — a draft — and the draft read
// back into the section. Pure, so that the mapping is tested both ways
// without a page: a document read into drafts and written back is the
// document it was, and every field the forms do not show — a provider's
// endpoints, a workload's `requires`, the grant lines — passes through
// untouched. The service validates the shape; what the forms check is what
// they can see, so that a refusal is said beside the field before the save.

import type { blueprint } from "@impire-io/sdk";

import { validSlug } from "../../address/address.ts";
import type { ProviderConfig } from "../../wire/auth.ts";
import { SETTINGS_FIELDS } from "../../wire/inference.ts";
import type { Setting } from "../../wire/solutions.ts";
import type { Rule as GuardRule } from "../../wire/guard.ts";
import type { Registration } from "../../wire/workloads.ts";
import {
  draftOf as ruleDraftOf,
  problems as ruleProblems,
  ruleOf,
  type Draft as RuleFormDraft,
} from "../guardrails/RuleForm.tsx";
import { egressOf, namesOf } from "../workloads/editors.tsx";
import {
  PROVIDER_FORM_KEYS,
  nowhere,
  providerDraftOf,
  providerFieldsOf,
  type ProviderDraft,
} from "../settings/provider.ts";

type Document = blueprint.Document;

/** The image names a requirement by image targets, as the design offers them: every model, every toolkit. */
export const IMAGE = { model: "inference", toolkit: "toolkit-mcp" } as const;

/** TARGET_WORDS say an image target in the design's words. */
export const TARGET_WORDS: Readonly<Record<string, string>> = {
  [IMAGE.model]: "every model",
  [IMAGE.toolkit]: "every toolkit",
};

// ——— values ———

/** canonical is a value as JSON with every set of fields in name order, so that two values compare by what they say. */
export function canonical(v: unknown): string {
  return JSON.stringify(v, (_k, x: unknown) =>
    typeof x === "object" && x !== null && !Array.isArray(x)
      ? Object.fromEntries(Object.entries(x).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))
      : x,
  );
}

/** sameValue reports whether two values say the same, whatever the order of their fields. */
export function sameValue(a: unknown, b: unknown): boolean {
  return canonical(a) === canonical(b);
}

/**
 * valueText is a field's value as the form shows it: text as itself, and
 * anything else — a number, a list, a set of fields — as JSON. Text that
 * would read back as something else (`true`, `42`, `"quoted"`) is shown
 * quoted, so that it reads back as the text it is.
 */
export function valueText(v: unknown): string {
  if (typeof v === "string") {
    if (v.trim() === "") {
      return v;
    }
    try {
      JSON.parse(v);
      return JSON.stringify(v);
    } catch {
      return v;
    }
  }
  return JSON.stringify(v);
}

/** valueOf reads a value as typed: JSON where it reads as JSON, the text itself otherwise. */
export function valueOf(text: string): unknown {
  const t = text.trim();
  if (t === "") {
    return text;
  }
  try {
    return JSON.parse(t) as unknown;
  } catch {
    return text;
  }
}

/** FieldRow is one top-level field of a settings document: its name and its value as typed. */
export interface FieldRow {
  field: string;
  value: string;
}

/** fieldRowsOf is a settings document as rows, in its order. */
export function fieldRowsOf(doc: Record<string, unknown> | undefined): FieldRow[] {
  return Object.entries(doc ?? {}).map(([field, v]) => ({ field, value: valueText(v) }));
}

/** fieldsDocument reads the rows back; a row with neither a name nor a value is skipped. */
export function fieldsDocument(rows: readonly FieldRow[]): {
  value: Record<string, unknown>;
  error?: string;
} {
  const out: Record<string, unknown> = {};
  for (const r of rows) {
    const f = r.field.trim();
    if (f === "" && r.value.trim() === "") {
      continue;
    }
    if (f === "") {
      return { value: out, error: "a value with no field name" };
    }
    if (f in out) {
      return { value: out, error: `${f} is named twice` };
    }
    out[f] = valueOf(r.value);
  }
  return { value: out };
}

/** FieldHint is a field a document's reader names, offered as the name is typed. */
export interface FieldHint {
  field: string;
  what: string;
}

/** TOOLKIT_FIELDS are the MCP runtime's top-level settings (02-DESIGN/08-toolkits/02-the-mcp-runtime.md § the settings document). */
export const TOOLKIT_FIELDS: readonly FieldHint[] = [
  { field: "url", what: "the server; its host is on the registration's egress" },
  { field: "name", what: "what the toolkit is called in the workspace" },
  { field: "scope", what: "shared, person or public" },
  { field: "auth-scheme", what: "how a call is authorised upstream" },
  { field: "tools", what: 'the allowlist, or ["*"]' },
  { field: "call-timeout", what: "a duration; 60s when absent" },
  { field: "max-in-flight", what: "16 when absent" },
];

/** MODEL_FIELDS are the inference instance's, with the two the design adds to it. */
export const MODEL_FIELDS: readonly FieldHint[] = [
  ...SETTINGS_FIELDS.map((f) => ({ field: f.field, what: f.what })),
  { field: "max-in-flight", what: "how many requests at once" },
];

/** solutionFields are the tenant-scoped settings a solution's manifest declares; a person's own are never a blueprint's. */
export function solutionFields(settings: readonly Setting[] | undefined): FieldHint[] {
  return (settings ?? [])
    .filter((s) => s.scope === "tenant" && s.secret !== true)
    .map((s) => ({ field: s.name, what: s.hint ?? s.label }));
}

// ——— sign-in ———

/** RowDraft is a claim-to-role row the parent requires. */
export interface RowDraft {
  claim: string;
  value: string;
  role: "admin" | "member";
}

/** SignInDraft is the sign-in section as typed: whether the workspace is given one, the provider, the rows, its own provider. */
export interface SignInDraft {
  on: boolean;
  provider: ProviderDraft;
  displayName: string;
  rows: RowDraft[];
  ownProvider: "allowed" | "refused";
  /** The provider's fields the form does not show, kept as they were. */
  rest: Record<string, unknown>;
  /** Whether the provider listed no surfaces — everywhere — so that it reads back unlisted. */
  unlisted: boolean;
  /** Whether the document said `own-provider` at all; allowed is the default when it did not. */
  saidOwn: boolean;
}

/** A new managed provider: an OpenID Connect issuer whose browser exchanges its own code, which is the only kind pushed down. */
const NEW_PROVIDER: Record<string, unknown> = { lane: "oidc", exchange: "public" };

function without(o: Record<string, unknown>, keys: readonly string[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(o)) {
    if (!keys.includes(k)) {
      out[k] = v;
    }
  }
  return out;
}

const SIGNIN_FORM_KEYS: readonly string[] = [...PROVIDER_FORM_KEYS, "display_name"];

export function signInDraft(s: blueprint.SignIn | undefined): SignInDraft {
  const p = (s?.provider ?? {}) as Partial<ProviderConfig> & Record<string, unknown>;
  return {
    on: s !== undefined,
    provider: providerDraftOf(s === undefined ? undefined : p),
    displayName: typeof p.display_name === "string" ? p.display_name : "",
    rows: (s?.rows ?? []).map((r) => ({
      claim: r.claim,
      value: r.value,
      role: r.role === "admin" ? "admin" : "member",
    })),
    ownProvider: s?.["own-provider"] === "refused" ? "refused" : "allowed",
    rest: s === undefined ? { ...NEW_PROVIDER } : without(p, SIGNIN_FORM_KEYS),
    unlisted: s === undefined || (p.surfaces ?? []).length === 0,
    saidOwn: s?.["own-provider"] !== undefined,
  };
}

export function signInOf(d: SignInDraft): { value?: blueprint.SignIn; error?: string } {
  if (!d.on) {
    return {};
  }
  const provider: Record<string, unknown> = { ...d.rest, ...providerFieldsOf(d.provider, d.unlisted) };
  if (d.displayName.trim() !== "") {
    provider.display_name = d.displayName.trim();
  }
  const out: blueprint.SignIn = { provider };
  const rows: blueprint.RequiredRow[] = [];
  for (const r of d.rows) {
    if (r.claim.trim() === "" && r.value.trim() === "") {
      continue;
    }
    rows.push({ claim: r.claim.trim(), value: r.value.trim(), role: r.role });
  }
  if (rows.length > 0) {
    out.rows = rows;
  }
  if (d.ownProvider === "refused" || d.saidOwn) {
    out["own-provider"] = d.ownProvider;
  }
  if (!/^https:\/\/[^\s]+$/.test(d.provider.issuer.trim())) {
    return {
      value: out,
      error: "The issuer is an https URL, exactly as the provider's discovery states it.",
    };
  }
  if (
    d.provider.clientId.trim() === "" &&
    d.provider.browserClientId.trim() === "" &&
    d.provider.terminalClientId.trim() === ""
  ) {
    return { value: out, error: "A client id is required." };
  }
  if (nowhere(d.provider)) {
    return { value: out, error: "Pick at least one place people may sign in." };
  }
  if (d.rest.exchange === "server-side") {
    return {
      value: out,
      error:
        "This provider's code is exchanged by Impire with a client secret, and a secret is never pushed down: only a provider whose browser exchanges its own code can be.",
    };
  }
  if (rows.some((r) => r.claim === "" || r.value === "")) {
    return { value: out, error: "Each rule needs a claim and a value." };
  }
  return { value: out };
}

// ——— required settings ———

/** RequiredDraft is one required document: what it lies under, and the fields it fixes. */
export interface RequiredDraft {
  target: string;
  fields: FieldRow[];
}

export function requiredDraft(r: blueprint.RequiredSetting): RequiredDraft {
  return { target: r.target, fields: fieldRowsOf(r.document) };
}

// ——— guardrails ———

/** RuleDraft is a required rule in the Guardrails form's own draft, with any field that form does not show. */
export interface RuleDraft {
  draft: RuleFormDraft;
  rest: Record<string, unknown>;
}

const RULE_KEYS: readonly string[] = ["id", "selector", "expression", "escalation", "enabled"];

export function ruleDraft(r: blueprint.Rule): RuleDraft {
  const rule: GuardRule = {
    id: r.id,
    selector: r.selector ?? "",
    expression: r.expression ?? "",
    escalation: r.escalation as GuardRule["escalation"],
    enabled: r.enabled === true,
  };
  return { draft: ruleDraftOf(rule), rest: without(r, RULE_KEYS) };
}

export function ruleOfDraft(d: RuleDraft): blueprint.Rule {
  return { ...d.rest, ...ruleOf(d.draft) };
}

/** ruleError is what the form can see is wrong with a rule; the workspace's guardrails service is the authority. */
export function ruleError(d: RuleFormDraft): string | undefined {
  if (d.id.trim() === "" || d.selector.trim() === "" || d.expression.trim() === "") {
    return "a guardrail needs a name, what it guards and the rule";
  }
  const p = Object.values(ruleProblems(d));
  return p[0];
}

// ——— workloads ———

/** WorkloadKind is what a required workload runs: a model, a toolkit, or an image the parent brings. */
export type WorkloadKind = "model" | "toolkit" | "image";

/** WorkloadDraft is a required workload as typed. */
export interface WorkloadDraft {
  handle: string;
  kind: WorkloadKind;
  /** The OCI reference, for an image the parent brings. */
  image: string;
  name: string;
  description: string;
  names: string;
  replicas: string;
  cpus: string;
  memory: string;
  egress: string[];
  /** The registration's fields the form does not show (`requires`), kept as they were. */
  rest: Record<string, unknown>;
  /** The resources' fields the form does not show. */
  resourcesRest: Record<string, unknown>;
}

const WORKLOAD_KEYS: readonly string[] = [
  "handle",
  "image",
  "names",
  "resources",
  "egress",
  "replicas",
  "name",
  "description",
];

function kindOf(image: string): WorkloadKind {
  return image === IMAGE.model ? "model" : image === IMAGE.toolkit ? "toolkit" : "image";
}

export function workloadDraft(w: blueprint.Workload): WorkloadDraft {
  const res = w.resources ?? {};
  const num = (v: unknown) => (typeof v === "number" ? String(v) : "");
  return {
    handle: w.handle,
    kind: kindOf(w.image),
    image: kindOf(w.image) === "image" ? w.image : "",
    name: w.name ?? "",
    description: w.description ?? "",
    names: (w.names ?? []).join(", "),
    replicas: w.replicas === undefined ? "" : String(w.replicas),
    cpus: num(res.cpus),
    memory: num(res.memory_mib),
    egress: (w.egress ?? []).map((e) => (typeof e === "string" ? e : JSON.stringify(e))),
    rest: without(w as unknown as Record<string, unknown>, WORKLOAD_KEYS),
    resourcesRest: without(res, ["cpus", "memory_mib"]),
  };
}

/** blankWorkload is a new required workload of a kind, its handle unique among the handles given. */
export function blankWorkload(kind: WorkloadKind, taken: readonly string[]): WorkloadDraft {
  return {
    handle: handleFor(kind === "model" ? "model" : kind === "toolkit" ? "toolkit" : "workload", taken),
    kind,
    image: "",
    name: "",
    description: "",
    names: "",
    replicas: "",
    cpus: "",
    memory: "",
    egress: [],
    rest: {},
    resourcesRest: {},
  };
}

/** handleFor is a handle from a name: lowercase letters, digits and dashes, unique among those taken, never an image name. */
export function handleFor(name: string, taken: readonly string[]): string {
  const base =
    name
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, "-")
      .replace(/^-+|-+$/g, "") || "workload";
  const reserved = (h: string) => taken.includes(h) || h === IMAGE.model || h === IMAGE.toolkit;
  if (!reserved(base)) {
    return base;
  }
  for (let i = 2; ; i++) {
    const h = `${base}-${String(i)}`;
    if (!reserved(h)) {
      return h;
    }
  }
}

/** validHandle is a subject token the steward finds the workload by again: no space, dot, * or >. */
export function validHandle(h: string): boolean {
  return /^[A-Za-z0-9_-]+$/.test(h) && h !== IMAGE.model && h !== IMAGE.toolkit;
}

function numberOf(text: string, integer: boolean): number | undefined | Error {
  const t = text.trim();
  if (t === "") {
    return undefined;
  }
  const n = Number(t);
  if (!Number.isFinite(n) || (integer && !Number.isInteger(n))) {
    return new Error(`${t} is not ${integer ? "a whole number" : "a number"}`);
  }
  return n;
}

export function workloadOf(d: WorkloadDraft): { value: blueprint.Workload; error?: string } {
  const image = d.kind === "model" ? IMAGE.model : d.kind === "toolkit" ? IMAGE.toolkit : d.image.trim();
  const w: blueprint.Workload = { handle: d.handle.trim(), image };
  let error: string | undefined;
  const fail = (e: string) => {
    error ??= e;
  };
  const names = namesOf(d.names);
  if (names instanceof Error) {
    fail(names.message);
  } else if (names.length > 0) {
    w.names = names;
  }
  const res: Record<string, unknown> = { ...d.resourcesRest };
  const cpus = numberOf(d.cpus, false);
  const memory = numberOf(d.memory, true);
  if (cpus instanceof Error) {
    fail(`virtual CPUs: ${cpus.message}`);
  } else if (cpus !== undefined) {
    res.cpus = cpus;
  }
  if (memory instanceof Error) {
    fail(`memory: ${memory.message}`);
  } else if (memory !== undefined) {
    res.memory_mib = memory;
  }
  if (Object.keys(res).length > 0) {
    w.resources = res;
  }
  const egress = egressOf(d.egress);
  if (egress instanceof Error) {
    fail(egress.message);
  } else if (egress.length > 0) {
    w.egress = egress;
  }
  Object.assign(w, d.rest);
  const replicas = numberOf(d.replicas, true);
  if (replicas instanceof Error || (replicas !== undefined && replicas < 0)) {
    fail("copies is a whole number, zero or more");
  } else if (replicas !== undefined) {
    w.replicas = replicas;
  }
  if (d.name.trim() !== "") {
    w.name = d.name.trim();
  }
  if (d.description.trim() !== "") {
    w.description = d.description.trim();
  }
  if (!validHandle(w.handle)) {
    fail(`${w.handle === "" ? "a workload" : w.handle}: the handle is one word of letters, digits, - and _`);
  }
  if (d.kind === "image" && (image === "" || /\s/.test(image) || !image.includes("/"))) {
    fail("an image you bring is an OCI reference on the install's registry list: registry/name:tag");
  }
  return error === undefined ? { value: w } : { value: w, error };
}

/**
 * fromOwn is a required workload and its required settings copied from one
 * of the parent's own models or toolkits: the image, its names, its
 * resources and egress, and its settings document as what the child must
 * read — never a key, which is sealed in Secrets.
 */
export function fromOwn(
  r: Pick<Registration, "name" | "description" | "names" | "resources" | "egress">,
  kind: "model" | "toolkit",
  settings: Record<string, unknown> | undefined,
  taken: readonly string[],
): { workload: WorkloadDraft; required?: RequiredDraft } {
  const w = blankWorkload(kind, taken);
  w.handle = handleFor(r.name, taken);
  w.name = r.name;
  w.description = r.description ?? "";
  w.names = (r.names ?? []).join(", ");
  w.cpus = String(r.resources.cpus);
  w.memory = String(r.resources.memory_mib);
  w.egress = [...(r.egress ?? [])];
  const fields = fieldRowsOf(settings);
  return { workload: w, required: fields.length > 0 ? { target: w.handle, fields } : undefined };
}

// ——— solutions ———

/** SolutionDraft is an installed solution as typed: its name, the version pinned, and the settings fixed. */
export interface SolutionDraft {
  name: string;
  version: string;
  fields: FieldRow[];
  rest: Record<string, unknown>;
}

export function solutionDraft(s: blueprint.Solution): SolutionDraft {
  return {
    name: s.name,
    version: s.version ?? "",
    fields: fieldRowsOf(s.settings),
    rest: without(s as unknown as Record<string, unknown>, ["name", "version", "settings"]),
  };
}

export function solutionOf(d: SolutionDraft): { value: blueprint.Solution; error?: string } {
  const s: blueprint.Solution = { name: d.name };
  if (d.version.trim() !== "") {
    s.version = d.version.trim();
  }
  const f = fieldsDocument(d.fields);
  if (Object.keys(f.value).length > 0) {
    s.settings = f.value;
  }
  Object.assign(s, d.rest);
  return f.error === undefined ? { value: s } : { value: s, error: `${d.name}: ${f.error}` };
}

// ——— limits ———

/** LimitsDraft is the limits as typed: each count empty for none, whether it may manage workspaces, which solutions it may install. */
export interface LimitsDraft {
  workspaces: string;
  storage: string;
  vcpus: string;
  /** Memory as typed, in MiB; the document holds bytes. */
  memory: string;
  workloads: string;
  /** Empty: the blueprint says nothing; yes or no. */
  manages: "" | "yes" | "no";
  /** Undefined: any solution; otherwise exactly these. */
  solutions: string[] | undefined;
  rest: Record<string, unknown>;
}

/**
 * The numeric limits, with the document's field and the form's words. The
 * compute limits are written under the plans' names, `vcpus` (a count) and
 * `memory` (bytes, typed in MiB); a blueprint stored with the earlier `vcpu`
 * and `memory-mib` is read and saved under the new names, never both
 * (02-DESIGN/17-plans/02-features-and-limits.md; impire-sdk-js blueprint.Limits).
 */
export const LIMIT_FIELDS = [
  { key: "workspaces", field: "workspaces", label: "Workspaces beneath it", integer: true },
  { key: "workloads", field: "workloads", label: "Workloads", integer: true },
  { key: "vcpus", field: "vcpus", label: "Virtual CPUs, in all", integer: true },
  { key: "memory", field: "memory", label: "Memory, in all (MiB)", integer: true },
  { key: "storage", field: "storage", label: "Storage room (bytes)", integer: true },
] as const;

const MIB = 1024 * 1024;

export function limitsDraft(l: blueprint.Limits | undefined): LimitsDraft {
  const num = (v: number | undefined) => (v === undefined ? "" : String(v));
  const vcpus = l?.vcpus ?? (l?.vcpu !== undefined ? Math.ceil(l.vcpu) : undefined);
  const memory =
    l?.memory !== undefined ? (l.memory < 0 ? -1 : Math.ceil(l.memory / MIB)) : l?.["memory-mib"];
  return {
    workspaces: num(l?.workspaces),
    storage: num(l?.storage),
    vcpus: num(vcpus),
    memory: num(memory),
    workloads: num(l?.workloads),
    manages: l?.["manages-workspaces"] === undefined ? "" : l["manages-workspaces"] ? "yes" : "no",
    solutions: l?.solutions === undefined ? undefined : [...l.solutions],
    rest: without((l ?? {}) as Record<string, unknown>, [
      "workspaces",
      "manages-workspaces",
      "storage",
      "vcpu",
      "memory-mib",
      "vcpus",
      "memory",
      "workloads",
      "solutions",
    ]),
  };
}

export function limitsOf(d: LimitsDraft): { value?: blueprint.Limits; error?: string } {
  const out: Record<string, unknown> = {};
  let error: string | undefined;
  for (const f of LIMIT_FIELDS) {
    const n = numberOf(d[f.key], f.integer);
    if (n instanceof Error) {
      error ??= `${f.label}: ${n.message}`;
    } else if (n !== undefined) {
      if (n < -1) {
        error ??= `${f.label}: zero or more, or -1 for no bound`;
      }
      out[f.field] = f.key === "memory" && n >= 0 ? n * MIB : n;
    }
    if (f.key === "workspaces" && d.manages !== "") {
      out["manages-workspaces"] = d.manages === "yes";
    }
  }
  if (d.solutions !== undefined) {
    out.solutions = d.solutions;
  }
  Object.assign(out, d.rest);
  // The document's own order (impire-sdk-go blueprint.Limits).
  const order = ["workspaces", "manages-workspaces", "storage", "workloads", "vcpus", "memory", "solutions"];
  const sorted: Record<string, unknown> = {};
  for (const k of [...order, ...Object.keys(out).filter((k) => !order.includes(k))]) {
    if (k in out) {
      sorted[k] = out[k];
    }
  }
  const value = Object.keys(sorted).length === 0 ? undefined : (sorted as blueprint.Limits);
  return error === undefined ? { value } : { value, error };
}

// ——— the document ———

/** Draft is a whole blueprint as forms, and whatever of it no form shows — the grant lines — kept as it was. */
export interface Draft {
  signIn: SignInDraft;
  required: RequiredDraft[];
  rules: RuleDraft[];
  workloads: WorkloadDraft[];
  solutions: SolutionDraft[];
  secrets: blueprint.Secret[];
  limits: LimitsDraft;
  rest: Record<string, unknown>;
}

/** The sections a form edits; `secrets` is sealed in its own list. */
export type FormSection = "sign-in" | "required" | "rules" | "workloads" | "solutions" | "secrets" | "limits";

const DOCUMENT_KEYS: readonly string[] = [
  "sign-in",
  "required",
  "rules",
  "workloads",
  "solutions",
  "secrets",
  "limits",
];

export function draftOf(d: Document | undefined): Draft {
  return {
    signIn: signInDraft(d?.["sign-in"]),
    required: (d?.required ?? []).map(requiredDraft),
    rules: (d?.rules ?? []).map(ruleDraft),
    workloads: (d?.workloads ?? []).map(workloadDraft),
    solutions: (d?.solutions ?? []).map(solutionDraft),
    secrets: [...(d?.secrets ?? [])],
    limits: limitsDraft(d?.limits),
    rest: without((d ?? {}) as Record<string, unknown>, DOCUMENT_KEYS),
  };
}

/** targetOf resolves a required document's target as the service does: a required workload's handle, an image name, a solution of the blueprint. */
export function targetOf(
  target: string,
  d: Pick<Draft, "workloads" | "solutions">,
): "workload" | "image" | "solution" | undefined {
  if (d.workloads.some((w) => w.handle.trim() === target)) {
    return "workload";
  }
  if (target === IMAGE.model || target === IMAGE.toolkit) {
    return "image";
  }
  if (d.solutions.some((s) => s.name === target)) {
    return "solution";
  }
  return undefined;
}

/**
 * documentOf reads the forms back into the document, in the document's own
 * order, with what each section's form can see is wrong said per section.
 */
export function documentOf(d: Draft): { document: Document; errors: Partial<Record<FormSection, string>> } {
  const doc: Record<string, unknown> = {};
  const errors: Partial<Record<FormSection, string>> = {};
  const fail = (s: FormSection, e: string | undefined) => {
    if (e !== undefined && errors[s] === undefined) {
      errors[s] = e;
    }
  };

  const si = signInOf(d.signIn);
  fail("sign-in", si.error);
  if (si.value !== undefined) {
    doc["sign-in"] = si.value;
  }

  const required: blueprint.RequiredSetting[] = [];
  const targets = new Set<string>();
  for (const r of d.required) {
    const f = fieldsDocument(r.fields);
    const words = targetWords(r.target, d);
    fail("required", f.error === undefined ? undefined : `${words}: ${f.error}`);
    if (targetOf(r.target, d) === undefined) {
      fail(
        "required",
        `${r.target === "" ? "a requirement" : r.target} lies under nothing in this blueprint: pick what it is for`,
      );
    }
    if (targets.has(r.target)) {
      fail("required", `${words} is required twice: put its fields in one`);
    }
    if (Object.keys(f.value).length === 0) {
      fail("required", `${words} fixes no field`);
    }
    targets.add(r.target);
    required.push({ target: r.target, document: f.value });
  }
  if (required.length > 0) {
    doc.required = required;
  }

  const rules = d.rules.map(ruleOfDraft);
  const ids = new Set<string>();
  for (const r of d.rules) {
    fail("rules", ruleError(r.draft));
    if (ids.has(r.draft.id.trim())) {
      fail("rules", `two guardrails are called ${r.draft.id.trim()}`);
    }
    ids.add(r.draft.id.trim());
  }
  if (rules.length > 0) {
    doc.rules = rules;
  }

  const workloads: blueprint.Workload[] = [];
  const handles = new Set<string>();
  for (const w of d.workloads) {
    const r = workloadOf(w);
    fail("workloads", r.error);
    if (handles.has(r.value.handle)) {
      fail("workloads", `two workloads have the handle ${r.value.handle}`);
    }
    handles.add(r.value.handle);
    workloads.push(r.value);
  }
  if (workloads.length > 0) {
    doc.workloads = workloads;
  }

  const solutions: blueprint.Solution[] = [];
  for (const s of d.solutions) {
    const r = solutionOf(s);
    fail("solutions", r.error);
    solutions.push(r.value);
  }
  if (solutions.length > 0) {
    doc.solutions = solutions;
  }

  for (const s of d.secrets) {
    if (targetOf(s.for, d) !== "workload" && targetOf(s.for, d) !== "solution") {
      fail("secrets", `${s.for}.${s.name} belongs to nothing in this blueprint any more: remove it`);
    }
  }
  if (d.secrets.length > 0) {
    doc.secrets = d.secrets;
  }

  const l = limitsOf(d.limits);
  fail("limits", l.error);
  if (l.value !== undefined) {
    doc.limits = l.value;
  }

  fail("limits", grantsError(d.rest.grants));

  Object.assign(doc, d.rest);
  return { document: doc, errors };
}

/**
 * grantsError is what the Limits tab can see is wrong with the grant lines
 * (12-workspace-hierarchy/02-grants.md): a capability of the platform's
 * three, a receiver named by its address, a control grant naming its solution,
 * and no line twice.
 */
export function grantsError(grants: unknown): string | undefined {
  if (grants === undefined) {
    return undefined;
  }
  if (!Array.isArray(grants)) {
    return "the grants are not a list of grant lines";
  }
  const seen = new Set<string>();
  for (const raw of grants as Partial<blueprint.Grant>[]) {
    const cap = raw.capability ?? "";
    const to = (raw.receiver ?? "").trim();
    if (cap !== "audit" && cap !== "usage" && cap !== "control") {
      return `a grant names ${cap === "" ? "no capability" : cap}: audit, usage or control`;
    }
    if (!validSlug(to)) {
      return to === ""
        ? `a ${cap} grant names no receiving workspace`
        : `${to} is not a workspace's address: lowercase letters, digits and hyphens, beginning with a letter`;
    }
    if (cap === "control" && (raw.solution ?? "").trim() === "") {
      return `the control grant to ${to} names no solution`;
    }
    const key = `${cap}>${to}>${cap === "control" ? (raw.solution ?? "") : ""}`;
    if (seen.has(key)) {
      return `${cap} goes to ${to} twice: keep one`;
    }
    seen.add(key);
  }
  return undefined;
}

/** targetWords says what a required document lies under: every model, every toolkit, a required workload by name, a solution. */
export function targetWords(target: string, d: Pick<Draft, "workloads" | "solutions">): string {
  const image = TARGET_WORDS[target];
  if (image !== undefined) {
    return image;
  }
  const w = d.workloads.find((x) => x.handle.trim() === target);
  if (w !== undefined) {
    return w.name.trim() !== "" ? w.name.trim() : w.handle;
  }
  if (d.solutions.some((s) => s.name === target)) {
    return `the solution ${target}`;
  }
  return target;
}

/** fieldHints are the fields a target's reader names: a model's, a toolkit's, a solution's declared settings. */
export function fieldHints(
  target: string,
  d: Pick<Draft, "workloads" | "solutions">,
  manifests: ReadonlyMap<string, readonly Setting[]>,
): readonly FieldHint[] {
  const kind = targetOf(target, d);
  if (kind === "image") {
    return target === IMAGE.model ? MODEL_FIELDS : TOOLKIT_FIELDS;
  }
  if (kind === "workload") {
    const w = d.workloads.find((x) => x.handle.trim() === target);
    return w?.kind === "model" ? MODEL_FIELDS : w?.kind === "toolkit" ? TOOLKIT_FIELDS : [];
  }
  if (kind === "solution") {
    return solutionFields(manifests.get(target));
  }
  return [];
}

/**
 * dropWorkload removes a required workload with what was only its: the
 * required settings that lie under its handle and the secrets sealed for it.
 */
export function dropWorkload(d: Draft, handle: string): Draft {
  return {
    ...d,
    workloads: d.workloads.filter((w) => w.handle !== handle),
    required: d.required.filter((r) => r.target !== handle),
    secrets: d.secrets.filter((s) => s.for !== handle),
  };
}

/** dropSolution removes a solution with its required settings and its secrets. */
export function dropSolution(d: Draft, name: string): Draft {
  return {
    ...d,
    solutions: d.solutions.filter((s) => s.name !== name),
    required: d.required.filter((r) => r.target !== name),
    secrets: d.secrets.filter((s) => s.for !== name),
  };
}

/** renameHandle follows a new workload's handle into what names it: its required settings and secrets. */
export function renameHandle(d: Draft, from: string, to: string): Draft {
  return {
    ...d,
    workloads: d.workloads.map((w) => (w.handle === from ? { ...w, handle: to } : w)),
    required: d.required.map((r) => (r.target === from ? { ...r, target: to } : r)),
    secrets: d.secrets.map((s) => (s.for === from ? { ...s, for: to } : s)),
  };
}

/**
 * libraryDocumentOf reads the forms back into a named blueprint
 * (01-the-blueprint.md § named blueprints): the document, its secrets as
 * slots — what each is for and its name — and never a value, since a value
 * is sealed for one workspace's vault and each workspace enters its own.
 */
export function libraryDocumentOf(d: Draft): {
  document: blueprint.LibraryDocument;
  errors: Partial<Record<FormSection, string>>;
} {
  const { document, errors } = documentOf(d);
  return { document: toLibrary(document), errors };
}

/** toLibrary is a document as a named blueprint: each secret its slot, its value dropped. */
export function toLibrary(doc: Document): blueprint.LibraryDocument {
  const { secrets, ...rest } = doc;
  const out: blueprint.LibraryDocument = { ...rest };
  if (secrets !== undefined && secrets.length > 0) {
    out.secrets = secrets.map((s) => ({ for: s.for, name: s.name }));
  }
  return out;
}

/** slotOfSecret is a slot as the forms keep it: a secret with no value, never sent as one. */
export function slotOfSecret(forHandle: string, name: string): blueprint.Secret {
  return { for: forHandle, name, sealed: "", "sealed-by": "", version: 0 };
}

/** libraryDraftOf is a named blueprint as forms: its slots kept as secrets with no value. */
export function libraryDraftOf(d: blueprint.LibraryDocument | undefined): Draft {
  const { secrets, ...rest } = d ?? {};
  const draft = draftOf(rest);
  draft.secrets = (secrets ?? []).map((s) => slotOfSecret(s.for, s.name));
  return draft;
}
